const DEFAULT_EXPANDED_ITEMS = [
  "Your Programs will continue to be available on the Dashboard",
  "Program Library resources will remain accessible in the Library",
];

/**
 * Flag payloads are hand-authored JSON in GitOps. Destructuring defaults only
 * cover `undefined`, so nulls and wrong types reach the field assignments and
 * blow up at render. Coerce at the boundary instead.
 */
const toText = (value, fallback = "") => {
  if (typeof value === "string") return value;
  if (value == null) return fallback;
  return String(value);
};

const toStringList = (value) =>
  Array.isArray(value) ? value.filter((v) => typeof v === "string") : [];

/**
 * Coerces a flag-payload value to a UTC Date, or null when it is absent, of
 * an unexpected type, or unparseable.
 *
 * An unparseable date is deliberately treated as "no bound" rather than a
 * hard stop: a typo in the GitOps payload should not silently hide a banner
 * Product expects to be live. Mirrors mobile's _asUtcDate exactly
 * (program_dashboard_core/lib/src/models/app_notification.dart, ADO 189200).
 */
const toUtcDate = (value) => {
  if (typeof value !== "string") return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

class AppNotification {
  constructor({
    enabled = false,
    collapsedText = "",
    expandedText = "",
    reassuranceText = "",
    effectiveDate = null,
    expandedItems = DEFAULT_EXPANDED_ITEMS,
    routes = [],
    excludedRoutes = [],
    startDate = null,
    endDate = null,
  } = {}) {
    this.enabled = Boolean(enabled);
    this.collapsedText = toText(collapsedText);
    this.expandedText = toText(expandedText);
    this.reassuranceText = toText(reassuranceText) || this.expandedText;
    this.effectiveDate = effectiveDate ?? null;
    this.expandedItems = Array.isArray(expandedItems) ? expandedItems : DEFAULT_EXPANDED_ITEMS;
    /**
     * Routes this announcement is scoped to. Empty means every route.
     *
     * App.vue used to hardcode `if (this.$route.name !== "programs") return
     * null`, which was right while this flag carried only the
     * Programs-retirement notice and silently wrong once the same flag also
     * carried a platform-wide rebrand announcement (185519 AC7 wants that one
     * shown on login, not on one page). Scope belongs to the announcement, so
     * it now travels in the payload. Mirrors mobile's `AppNotification.tabs`
     * (ADO 189200).
     */
    this.routes = toStringList(routes);
    /**
     * Routes this announcement must not appear on, even when `routes` is
     * empty (site-wide) or would otherwise allow the route. Empty means no
     * exclusions. A wrong-typed value coerces to empty so a GitOps typo
     * cannot hide the banner everywhere (ADO 192190).
     */
    this.excludedRoutes = toStringList(excludedRoutes);
    /**
     * ADO 189199 AC1-AC4 (185519 AC7's visibility window). A null bound means
     * no bound (decision, 2026-08-25): an open-ended banner is expressed by
     * omitting the end date rather than by a second flag. Mirrors mobile's
     * AppNotification.startDate/endDate (ADO 189200) exactly.
     */
    this.startDate = toUtcDate(startDate);
    this.endDate = toUtcDate(endDate);
  }

  /**
   * Whether this announcement has valid content to render at all.
   *
   * Distinct from `enabled`: a payload can be flagged on and still be
   * unusable, because a missing or wrong-typed collapsedText coerces to an
   * empty string. Gating on `enabled` alone would paint an empty bar.
   */
  get isDisplayable() {
    return this.enabled && this.collapsedText.trim().length > 0;
  }

  /**
   * Whether the banner should be shown at `now`.
   *
   * Combines content validity (isDisplayable) with the configured visibility
   * window. Both bounds are inclusive: AC1 says the banner shows once the
   * start date "has been reached", and AC3 says it is absent only after the
   * end date "has passed", so the boundary instants themselves are inside the
   * window. `now` is passed in rather than read from the clock so the window
   * is directly testable.
   */
  isVisibleAt(now) {
    if (!this.isDisplayable) return false;
    if (this.startDate && now < this.startDate) return false;
    if (this.endDate && now > this.endDate) return false;
    return true;
  }

  /**
   * Whether this announcement may appear on the route named [routeName].
   * An exclusion wins over an allowlist entry.
   */
  appearsOnRoute(routeName) {
    if (this.excludedRoutes.includes(routeName)) return false;
    return this.routes.length === 0 || this.routes.includes(routeName);
  }

  static fromFlagData(data) {
    if (data == null) return null;
    return new AppNotification(data);
  }

  interpolateDate() {
    return this.collapsedText.replace("{date}", this.effectiveDate ?? "");
  }
}

export default AppNotification;
